/**
 * Dinheiro de escrow que tem de voltar para a conta do cliente.
 *
 * Existe porque desde 03/10/2026 o cliente paga antes de assinar, e um
 * contrato pago pode nunca ser assinado. Enquanto não houver gateway, a
 * transferência é feita por uma pessoa num banco — e o que este ecrã garante é
 * que nenhuma se perca pelo caminho: fica aqui, com os dias à espera à vista,
 * até alguém pôr a referência da transferência.
 *
 * A referência é obrigatória de propósito. Um botão «marcar como devolvido»
 * sem ela deixa o registo a dizer que o dinheiro saiu com base na palavra de
 * quem carregou — e é a primeira coisa que se vai procurar quando um cliente
 * telefonar a dizer que não recebeu nada.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Undo2, Clock, AlertTriangle, Check } from 'lucide-react';
import api from '../../utils/api';

const kz = (v) => `${Number(v || 0).toLocaleString('pt-PT')} Kz`;
const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-PT', { timeZone: 'Africa/Luanda' }) : '—');

const QUEM = {
  CLIENTE: 'o cliente desistiu',
  PRESTADOR: 'a empresa desistiu',
  DOZY: 'decisão da Dozy',
};

const DevolucoesPage = () => {
  const [fila, setFila] = useState(null);
  const [vencidos, setVencidos] = useState(null);
  const [refs, setRefs] = useState({});
  const [aGravar, setAGravar] = useState(null);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => {
    api.get('/projects/devolucoes/pendentes').then((r) => setFila(r.data)).catch(() => setFila([]));
    api.get('/projects/devolucoes/por-assinar')
      .then((r) => setVencidos(r.data))
      .catch(() => setVencidos({ prazo_dias: 5, contratos: [] }));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const confirmar = async (d) => {
    setErro('');
    setAGravar(d.devolucao_id);
    try {
      await api.post(`/projects/devolucoes/${d.devolucao_id}/confirmar`,
        { referencia: refs[d.devolucao_id] });
      carregar();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível confirmar a devolução.');
    } finally {
      setAGravar(null);
    }
  };

  const pedir = async (c) => {
    const motivo = window.prompt(
      `Devolver ${kz(c.escrow_kz)} ao cliente da obra ${c.codigo}.\n\n`
      + 'Motivo (o cliente e a empresa vão ler):');
    if (!motivo) return;
    setErro('');
    try {
      await api.post(`/projects/${c.project_id}/contrato/devolucao`,
        { quem_desistiu: 'DOZY', motivo });
      carregar();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível abrir a devolução.');
    }
  };

  if (!fila || !vencidos) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const total = fila.reduce((s, d) => s + Number(d.valor_kz || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Undo2 className="w-6 h-6 text-sky-400" /> Devoluções do valor protegido
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Obras que o cliente pagou e que nunca chegaram a arrancar. Devolve-se
          por inteiro: a visita técnica já foi paga à parte e a obra não começou,
          por isso não há comissão a cobrar.
        </p>
      </div>

      {erro && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {erro}
        </div>
      )}

      {/* ── Por transferir ─────────────────────────────────── */}
      <section className="space-y-2">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
            Por transferir
          </h2>
          {fila.length > 0 && (
            <span className="text-sm text-sky-300 font-semibold tabular-nums">{kz(total)}</span>
          )}
        </div>

        {fila.length === 0 ? (
          <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
            Nenhuma devolução por fazer.
          </div>
        ) : fila.map((d) => (
          <div key={d.devolucao_id} className="p-4 rounded-xl border border-slate-800 bg-slate-900 space-y-3">
            <div className="flex gap-3 items-start flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-semibold text-slate-100">{d.titulo}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {d.codigo} · {d.contrato} · {d.empresa || 'sem empresa'}
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  {d.cliente}
                  {d.cliente_telefone && (
                    <a href={`tel:${d.cliente_telefone}`} className="ml-2 text-sky-400 font-mono">
                      {d.cliente_telefone}
                    </a>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-sky-300 tabular-nums">{kz(d.valor_kz)}</div>
                <div className={`text-xs inline-flex items-center gap-1 ${
                  d.dias_espera >= 3 ? 'text-amber-400' : 'text-slate-500'}`}>
                  <Clock className="w-3 h-3" />
                  {d.dias_espera === 0 ? 'pedida hoje' : `à espera há ${d.dias_espera} dias`}
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-400">
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                {QUEM[d.quem_desistiu] || d.quem_desistiu}
              </span>
              <span className="ml-2">{d.motivo}</span>
            </div>

            <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-800">
              <input
                value={refs[d.devolucao_id] || ''}
                onChange={(e) => setRefs((r) => ({ ...r, [d.devolucao_id]: e.target.value }))}
                placeholder="Referência da transferência"
                className="flex-1 min-w-[200px] min-h-[44px] px-3 rounded-lg bg-slate-950 border border-slate-700
                           text-slate-100 text-sm placeholder:text-slate-600"
              />
              <button
                onClick={() => confirmar(d)}
                disabled={!String(refs[d.devolucao_id] || '').trim() || aGravar === d.devolucao_id}
                className="min-h-[44px] px-4 rounded-lg bg-sky-600 text-white text-sm font-medium
                           inline-flex items-center gap-2 disabled:opacity-40"
              >
                {aGravar === d.devolucao_id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Check className="w-4 h-4" />}
                Já transferi
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* ── Pagos e nunca assinados ────────────────────────── */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
          Pagos e por assinar há mais de {vencidos.prazo_dias} dias
        </h2>
        <p className="text-xs text-slate-500 max-w-2xl">
          O dinheiro está protegido na Dozy e falta uma assinatura. Telefonar primeiro:
          pode ser só uma assinatura esquecida. A devolução cancela a obra.
        </p>

        {vencidos.contratos.length === 0 ? (
          <div className="p-6 rounded-xl border border-slate-800 bg-slate-900 text-center text-slate-400 text-sm">
            Nenhum contrato pago à espera de assinatura.
          </div>
        ) : vencidos.contratos.map((c) => (
          <div key={c.contrato_id} className="p-4 rounded-xl border border-amber-500/20 bg-slate-900
                                              flex gap-3 items-center flex-wrap">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-slate-100">{c.titulo}</div>
              <div className="text-xs text-slate-500 font-mono">
                {c.codigo} · {c.numero} · pago em {dt(c.escrow_pago_em)}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                falta assinar: {[!c.cliente_assinou && 'cliente', !c.prestador_assinou && 'empresa']
                  .filter(Boolean).join(' e ')}
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-slate-200 tabular-nums">{kz(c.escrow_kz)}</div>
              <div className="text-xs text-amber-400">{c.dias_desde_pagamento} dias</div>
            </div>
            <button
              onClick={() => pedir(c)}
              className="min-h-[44px] px-4 rounded-lg border border-slate-700 text-slate-200 text-sm"
            >
              Devolver
            </button>
          </div>
        ))}
      </section>
    </div>
  );
};

export default DevolucoesPage;
